import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase, UserProfile, getUserProfile, updateLastLogin, getUserPermissions, isSupabaseConfigured } from '../lib/supabase'
import { useNavigate, useLocation } from 'react-router-dom'

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  permissions: string[]
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
  hasPermission: (permission: string) => boolean
  isDemoMode: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Guest data for debugging without login
const GUEST_PERMISSIONS = [
  'dashboard:read',
  'experiments:read',
  'backlog:read',
  'market-insights:read'
]

const GUEST_USER_PROFILE: UserProfile = {
  id: 'guest-user-id',
  email: 'guest@adapt.com',
  full_name: 'Guest User',
  avatar_url: undefined,
  department: 'Guest',
  role_id: 'guest-role-id',
  is_active: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  role_name: 'Guest',
  role_level: 1
}

// Demo data for when Supabase is not configured
const DEMO_PERMISSIONS = [
  'dashboard:read', 'dashboard:write',
  'experiments:read', 'experiments:write', 'experiments:delete',
  'backlog:read', 'backlog:write', 'backlog:delete',
  'market-insights:read', 'market-insights:write',
  'users:read', 'users:write',
  'ai-copilot:use',
  'reports:export',
  'settings:read', 'settings:write'
]

const DEMO_USER_PROFILE: UserProfile = {
  id: 'demo-user-id',
  email: 'demo@adapt.com',
  full_name: 'Demo User',
  avatar_url: undefined,
  department: 'Product',
  role_id: 'demo-role-id',
  is_active: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  role_name: 'Executive',
  role_level: 4
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [isDemoMode, setIsDemoMode] = useState(!isSupabaseConfigured)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    setIsDemoMode(!isSupabaseConfigured)

    if (!isSupabaseConfigured) {
      // In demo mode, simulate a logged-in user
      setTimeout(() => {
        setUserProfile(DEMO_USER_PROFILE)
        setPermissions(DEMO_PERMISSIONS)
        setLoading(false)
      }, 1000)
      return
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        loadUserData(session.user.id)
      } else {
        // For debugging: use guest profile instead of redirecting to login
        setUserProfile(GUEST_USER_PROFILE)
        setPermissions(GUEST_PERMISSIONS)
        setLoading(false)
        
        // Comment out the redirect for debugging
        // if (location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/pricing') {
        //   navigate('/login')
        // }
      }
    }).catch((error) => {
      console.error('Error getting session:', error)
      // For debugging: use guest profile instead of redirecting to login
      setUserProfile(GUEST_USER_PROFILE)
      setPermissions(GUEST_PERMISSIONS)
      setLoading(false)
      
      // Comment out the redirect for debugging
      // if (location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/pricing') {
      //   navigate('/login')
      // }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null)
      
      if (session?.user) {
        await loadUserData(session.user.id)
        
        // Update last login on sign in
        if (event === 'SIGNED_IN') {
          await updateLastLogin(session.user.id)
        }

        // Redirect to dashboard if on login or signup page
        if (location.pathname === '/login' || location.pathname === '/signup') {
          navigate('/')
        }
      } else {
        // For debugging: use guest profile instead of clearing and redirecting
        setUserProfile(GUEST_USER_PROFILE)
        setPermissions(GUEST_PERMISSIONS)
        setLoading(false)
        
        // Comment out the redirect for debugging
        // if (location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/pricing') {
        //   navigate('/login')
        // }
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const loadUserData = async (userId: string) => {
    try {
      setLoading(true)
      
      // Load user profile
      const profile = await getUserProfile(userId)
      setUserProfile(profile)
      
      // Load user permissions
      const userPermissions = await getUserPermissions(userId)
      setPermissions(userPermissions.map((p: any) => p.permission_name))
      
    } catch (error) {
      console.error('Error loading user data:', error)
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    if (isDemoMode) {
      // In demo mode, simulate successful login with demo accounts
      if (email === 'executive@adapt.com' && password === 'password123') {
        setUserProfile({
          ...DEMO_USER_PROFILE,
          email,
          role_name: 'Executive',
          role_level: 4
        })
        setPermissions(DEMO_PERMISSIONS)
        return { error: null }
      } else if (email === 'manager@adapt.com' && password === 'password123') {
        setUserProfile({
          ...DEMO_USER_PROFILE,
          email,
          role_name: 'Manager',
          role_level: 3
        })
        setPermissions(DEMO_PERMISSIONS.filter(p => !p.includes('settings')))
        return { error: null }
      } else if (email === 'analyst@adapt.com' && password === 'password123') {
        setUserProfile({
          ...DEMO_USER_PROFILE,
          email,
          role_name: 'Analyst',
          role_level: 2
        })
        setPermissions(['dashboard:read', 'experiments:read', 'experiments:write', 'backlog:read', 'backlog:write', 'market-insights:read', 'ai-copilot:use'])
        return { error: null }
      } else if (email === 'viewer@adapt.com' && password === 'password123') {
        setUserProfile({
          ...DEMO_USER_PROFILE,
          email,
          role_name: 'Viewer',
          role_level: 1
        })
        setPermissions(['dashboard:read', 'experiments:read', 'backlog:read', 'market-insights:read'])
        return { error: null }
      }
      
      return { error: { message: 'Invalid login credentials. Try: executive@adapt.com / password123' } }
    }
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      return { error }
    } catch (error) {
      console.error('Network error during sign in:', error)
      return { error: { message: 'Network error. Please check your connection and try again.' } }
    }
  }

  const signUp = async (email: string, password: string, fullName: string) => {
    if (isDemoMode) {
      return { error: { message: 'Sign up is disabled in demo mode. Please use one of the demo accounts.' } }
    }
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      })
      return { error }
    } catch (error) {
      console.error('Network error during sign up:', error)
      return { error: { message: 'Network error. Please check your connection and try again.' } }
    }
  }

  const signOut = async () => {
    if (isDemoMode) {
      setUserProfile(null)
      setPermissions([])
      navigate('/login')
      return
    }
    
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Error signing out:', error)
      }
      navigate('/login')
    } catch (error) {
      console.error('Network error during sign out:', error)
      navigate('/login')
    }
  }

  const hasPermission = (permission: string) => {
    return permissions.includes(permission)
  }

  const value = {
    user,
    userProfile,
    permissions,
    loading,
    signIn,
    signUp,
    signOut,
    hasPermission,
    isDemoMode,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}